import * as angular from 'angular';
import * as _ from 'lodash';

import { SeriesSelectorViewModel, ComponentDefinition, IdentifierType, KeyValuePair, SelectorType, ISortConfiguration, ObjectType } from '../../models';
import { SeriesConfiguration } from '../../widgets';
import { groupIdentifierSeparator, wtDroppableDrop, seriesAdded, Helper } from '../../common';
import { ISeriesSelectorService, ISeriesService } from '../../services';
import { ChartByIdentifierSeriesConfiguration } from './ChartByIdentifierSeriesConfiguration';
import { ChartByIdentifierWidget } from '../../widgets/widgets.index';

export interface OptionalColumns {
    stepped: boolean;
    minMax: boolean;
}

interface ISeriesChartByIdentifier {
    id: string;
    series: ChartByIdentifierSeriesConfiguration[];
    seriesSelectorViewModels: SeriesSelectorViewModel[];
    symbols: string[];
    type: string;

    setSymbol: (series: any, newSymbol: string) => void;
    getName: (guid: string) => string;
    remove: (series: SeriesConfiguration) => void;
    setXAxis: (guid: string) => void;
}

class SeriesChartByIdentifierController implements ISeriesChartByIdentifier {
    static controllerId = 'WtSeriesChartByIdentifierController';

    id: string;
    series: ChartByIdentifierSeriesConfiguration[];
    seriesSelectorViewModels: SeriesSelectorViewModel[];
    selectedSerie: SeriesSelectorViewModel;
    symbols: string[];
    type: string;
    isBindable: boolean;
    identifierTypes: KeyValuePair<string, IdentifierType>[];
    sortConfig: ISortConfiguration = {
        animation: 150,
        onSort: evt => this.sort(evt),
        handle: '.drag-handle'
    };
    item?: ChartByIdentifierWidget;

    readonly optionalColumns: OptionalColumns;

    static $inject = [
        '$scope',
        'seriesSelectorService',
        'seriesService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _seriesSelectorService: ISeriesSelectorService,
        private _seriesService: ISeriesService
    ) {
    }

    $onInit() {
        this.symbols = Helper.getSymbols();

        const self = this;
        this._$scope.$on(wtDroppableDrop, (event, data) => {
            if (data.itemId) {
                const guid = data.itemId.replace('id', '');
                if (data.binId) {
                    const bin = data.binId.split('+');
                    self.updateSerie(bin[0], bin[1], guid);
                } else {
                    self.addSeries(guid);
                }
            }
        });

        this._$scope.$on(seriesAdded,
            (event, data) => {
                self.addSeries(data.guid);
            });

        this._seriesSelectorService
            .getSeriesSelectorViewModels()
            .then(response => {
                self.seriesSelectorViewModels = response;
                self.identifierTypes = response.map(svm => {
                    return {
                        key: svm.guid,
                        value: svm.identifierType
                    };
                });
                self.bindSeries();
            });

        this._$scope.$watch(() => this.series, () => self.bindSeries());
    }

    // bind local stored series with data from live series
    private bindSeries() {
        if (this.series && this.seriesSelectorViewModels) {
            this.series.forEach(s => {
                const vm = _.find(this.seriesSelectorViewModels, d => s.guid === d.guid);
                s.min = vm.min;
                s.max = vm.max;
                s.hasMin = this.hasMinMax(s.min);
                s.hasMax = this.hasMinMax(s.max);
            });
        }
    }

    private sort(evt) {
        evt.models.forEach((s, i) => s.sortOrder = i);
        this.series = angular.copy(evt.models);
    }

    canBind(series: SeriesSelectorViewModel) {
        return this.isBindable && series.type === SelectorType.identifier;
    }

    getName(guid: string): string {
        let name = '';
        const svm = _.find(this.seriesSelectorViewModels, s => s.guid === guid);
        if (svm) {
            name = svm.groupUniqueName
                ? svm.groupUniqueName + groupIdentifierSeparator + svm.name
                : svm.name;
        }
        return name;
    }

    remove(series: SeriesConfiguration): void {
        _.remove(this.series, s => s.guid === series.guid && s.value === series.value);
        this.maintainOrder();
    }

    setXAxis(guid: string): void {
        this.series.forEach(s => s.isXAxis = (s.guid === guid));
    }

    setSymbol(series: SeriesConfiguration, newSymbol: string): void {
        series.symbol = newSymbol;
    }

    identifierType(guid: string): IdentifierType {
        const found = _.find(this.identifierTypes, t => t.key === guid);
        let result = IdentifierType.Tag;
        if (found) {
            result = found.value;
        }
        return result;
    }

    select(serie): void {
        this.selectedSerie = serie;
    }


    private maintainOrder() {
        this.series.forEach((s, i) => s.sortOrder = (i + 1));
    }

    private hasMinMax(value) {
        return value !== undefined && value !== null;
    }

    //TODO same as wtSeriesTableDirective
    private addSeries(guid: string): void {
        const svm = _.find(this.seriesSelectorViewModels, (s: SeriesSelectorViewModel) => s.guid === guid);
        const length = this.series.length;
        const serie = new ChartByIdentifierSeriesConfiguration(
            guid,
            Helper.getColor(length),
            Helper.getSymbol(length)
        );

        if (svm) {
            serie.type = svm.type;
            serie.min = svm.min;
            serie.max = svm.max;
            serie.hasMin = this.hasMinMax(svm.min);
            serie.hasMax = this.hasMinMax(svm.max);
            serie.hasReconciledValue = svm.hasReconciledValue;
            serie.isConstraint = svm.isConstraint;
            serie.sortOrder = this.series.length;
            serie.value = this.getValueType(serie, svm);
            if (!this.identifierTypes) {
                this.identifierTypes = [];
            }
        }
        const existing = this.series.filter(s => s.guid === guid);
        let canAdd = false;
        if (existing && existing.length > 0) {
            const possibleValues = this._seriesSelectorService.getValues(svm as any, true);
            const value = _.find(possibleValues, v => _.every(existing, e => e.value !== v.key));
            if (value) {
                serie.value = value.key;
                canAdd = true;
            }
        } else {
            canAdd = true;
        }
        if (canAdd) {
            this._seriesService.bind(serie, svm.groupAbsolutePath);
            this.series.push(serie);
        }
        this.maintainOrder();
    }

    private getValueType(serie: SeriesConfiguration, svm: SeriesSelectorViewModel): string {
        const availableValueTypes = this._seriesSelectorService.getValues(svm as any, true);
        let result = serie.value;

        if (availableValueTypes && availableValueTypes.length > 0) {
            result = availableValueTypes[0].key;
        }

        return result;
    }

    private updateSerie(oldValue: string, oldGuid: string, newGuid: string) {
        const svm = _.find(this.seriesSelectorViewModels, s => s.guid === newGuid);
        const old = _.find(this.series, s => s.guid === oldGuid && s.value === oldValue);
        const newValueType = this._seriesSelectorService.getAvailableValueType(svm, this.series, old);
        const isIdentifier = svm.objectType === ObjectType.Identifier;

        if (newValueType) {
            old.guid = newGuid;
            old.value = newValueType;
            old.type = svm.type;
            old.isBoundToGroup = isIdentifier && old.isBoundToGroup;
        }
    }
}

export const wtSeriesChartByIdentifier: ComponentDefinition = {
    componentId: 'wtSeriesChartByIdentifier',
    controller: SeriesChartByIdentifierController,
    controllerAs: 'vm',
    template: require('./seriesChartByIdentifier.component.html'),
    bindings: {
        id: '@',
        item: '<?',
        isBindable: '=',
        optionalColumns: '<',
        series: '=',
        type: '@',
    }
};